import { useState } from 'react';
import { Flex, Box, Button, Menu, Portal } from '@chakra-ui/react';
import { LuGlobe, LuCheck, LuChevronRight, LuChevronDown } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { supportedLocales, type SupportedLocale } from '../i18n';

export const localeLabels: Record<SupportedLocale, string> = {
  en: 'English',
  es: 'Español',
  tr: 'Türkçe',
};

export const LanguageMenuItems = () => {
  const { i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Menu.Item
        value="language-toggle"
        fontSize="13px"
        fontWeight="400"
        color="neutral.400"
        borderRadius="6px"
        px="12px"
        py="8px"
        _hover={{
          backgroundColor: 'neutral.700',
          color: 'neutral.100',
        }}
        cursor="pointer"
        closeOnSelect={false}
        onClick={() => setExpanded(!expanded)}
      >
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <Flex alignItems="center" gap="10px">
            <LuGlobe style={{ width: 15, height: 15 }} />
            {localeLabels[i18n.language as SupportedLocale] || localeLabels.en}
          </Flex>
          {expanded ? (
            <LuChevronDown style={{ width: 14, height: 14 }} />
          ) : (
            <LuChevronRight style={{ width: 14, height: 14 }} />
          )}
        </Flex>
      </Menu.Item>
      {expanded && (
        <Box pl="12px">
          {supportedLocales.map((locale) => (
            <Menu.Item
              key={locale}
              value={`lang-${locale}`}
              onClick={() => i18n.changeLanguage(locale)}
              fontSize="13px"
              fontWeight="400"
              color={i18n.language === locale ? 'neutral.100' : 'neutral.400'}
              backgroundColor={i18n.language === locale ? 'neutral.700' : 'transparent'}
              borderRadius="6px"
              px="12px"
              py="6px"
              _hover={{
                backgroundColor: 'neutral.700',
                color: 'neutral.100',
              }}
              cursor="pointer"
              closeOnSelect={false}
            >
              <Flex alignItems="center" justifyContent="space-between" width="100%">
                {localeLabels[locale]}
                {i18n.language === locale && <LuCheck style={{ width: 14, height: 14 }} />}
              </Flex>
            </Menu.Item>
          ))}
        </Box>
      )}
    </>
  );
};

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button
          height="fit-content"
          py="8px"
          px="12px"
          fontWeight="400"
          fontSize="13px"
          color="neutral.500"
          backgroundColor="transparent"
          borderWidth="0"
          borderRadius="6px"
          _hover={{
            backgroundColor: 'neutral.800',
            color: 'neutral.100',
          }}
          justifyContent="flex-start"
        >
          <Flex alignItems="center" gap="10px">
            <LuGlobe style={{ width: 15, height: 15 }} />
            {localeLabels[i18n.language as SupportedLocale] || localeLabels.en}
          </Flex>
        </Button>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            backgroundColor="neutral.800"
            borderWidth="1px"
            borderColor="neutral.700"
            borderRadius="8px"
            minWidth="180px"
            p="8px"
          >
            {supportedLocales.map((locale) => (
              <Menu.Item
                key={locale}
                value={locale}
                onClick={() => i18n.changeLanguage(locale)}
                fontSize="13px"
                fontWeight="400"
                color={i18n.language === locale ? 'neutral.100' : 'neutral.400'}
                backgroundColor={i18n.language === locale ? 'neutral.700' : 'transparent'}
                borderRadius="6px"
                px="12px"
                py="8px"
                _hover={{
                  backgroundColor: 'neutral.700',
                  color: 'neutral.100',
                }}
                cursor="pointer"
              >
                <Flex alignItems="center" justifyContent="space-between" width="100%">
                  {localeLabels[locale]}
                  {i18n.language === locale && <LuCheck style={{ width: 14, height: 14 }} />}
                </Flex>
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

export default LanguageSwitcher;
